import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingSkeleton } from "@/components/states";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sun, LifeBuoy, GraduationCap, Loader2, Plus, X, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { createStudyPeriod } from "@/lib/holidays.functions";

export const Route = createFileRoute("/_app/breaks/")({
  head: () => ({
    meta: [
      { title: "Vacanze, recupero ed esami · Lumen" },
      { name: "description", content: "Organizza compiti delle vacanze, recuperi e preparazione agli esami con un piano giornaliero realistico." },
      { property: "og:title", content: "Vacanze, recupero ed esami · Lumen" },
      { property: "og:description", content: "Poco, regolare e intelligente: Lumen distribuisce il lavoro nel tempo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: BreaksHub,
});

type Kind = "summer" | "recovery" | "exam";
type AgeBand = "primary" | "middle" | "high" | "university";

type PeriodRow = {
  id: string; kind: Kind; title: string; subject_name: string | null;
  start_date: string | null; end_date: string | null; exam_date: string | null;
  readiness: number; status: string;
};

function BreaksHub() {
  const { t } = useTranslation();
  const [wizard, setWizard] = useState<Kind | null>(null);

  const periodsQ = useQuery({
    queryKey: ["study-periods"],
    queryFn: async () => {
      const { data } = await supabase
        .from("study_periods")
        .select("id,kind,title,subject_name,start_date,end_date,exam_date,readiness,status")
        .order("created_at", { ascending: false });
      return (data ?? []) as PeriodRow[];
    },
  });

  const periods = periodsQ.data;

  return (
    <div className="space-y-6 px-5 pt-8 pb-4">
      <PageHeader
        tone="peach"
        icon={<Sun className="h-5 w-5" />}
        title={t("breaks.title")}
        subtitle={t("breaks.subtitle")}
      />

      <div className="space-y-3">
        <BigButton
          icon={<Sun className="h-6 w-6" />}
          label={t("breaks.hub.summer")}
          desc={t("breaks.hub.summerDesc")}
          onClick={() => setWizard("summer")}
        />
        <BigButton
          icon={<LifeBuoy className="h-6 w-6" />}
          label={t("breaks.hub.recovery")}
          desc={t("breaks.hub.recoveryDesc")}
          onClick={() => setWizard("recovery")}
        />
        <BigButton
          icon={<GraduationCap className="h-6 w-6" />}
          label={t("breaks.hub.exam")}
          desc={t("breaks.hub.examDesc")}
          onClick={() => setWizard("exam")}
        />
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t("breaks.hub.active")}</h2>
        {periodsQ.isPending && <LoadingSkeleton rows={2} />}
        {periodsQ.isError && <ErrorState error={periodsQ.error} onRetry={() => periodsQ.refetch()} />}
        {!periodsQ.isPending && !periodsQ.isError && !periods?.length && (
          <p className="text-xs text-muted-foreground">{t("breaks.hub.empty")}</p>
        )}
        {periods?.map((p) => (
          <Link key={p.id} to="/breaks/$periodId" params={{ periodId: p.id }} className="block">
            <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-full text-xs">
                  {t(`breaks.hub.${p.kind}`)}
                </Badge>
                <span className="truncate text-sm font-semibold">{p.title}</span>
                <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
              </div>
              <Progress value={p.readiness} className="h-1.5" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{p.subject_name ?? ""}</span>
                <span>{p.readiness}%</span>
              </div>
            </Card>
          </Link>
        ))}
      </section>

      {wizard && <WizardDialog kind={wizard} onClose={() => setWizard(null)} />}
    </div>
  );
}

function BigButton({ icon, label, desc, onClick }: { icon: React.ReactNode; label: string; desc: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="w-full text-left">
      <Card className="card-soft flex items-start gap-4 rounded-3xl border-0 p-5 transition hover:bg-muted/60">
        <div className="rounded-2xl bg-primary/10 p-3 text-primary">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold">{label}</div>
          <p className="text-xs leading-relaxed text-muted-foreground">{desc}</p>
        </div>
      </Card>
    </button>
  );
}

/* ------------------------------- WIZARD ---------------------------------- */

const DOW = [1, 2, 3, 4, 5, 6, 7];

function WizardDialog({ kind, onClose }: { kind: Kind; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const create = useServerFn(createStudyPeriod);

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [hasDebts, setHasDebts] = useState<"yes" | "no" | "unknown" | null>(null);
  const [ageBand, setAgeBand] = useState<AgeBand>("middle");
  const [subject, setSubject] = useState("");
  const [scope, setScope] = useState<string>("topics");
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [examDate, setExamDate] = useState("");
  const [unknownDate, setUnknownDate] = useState(false);
  const [dailyMinutes, setDailyMinutes] = useState(kind === "summer" ? 30 : 45);
  const [restDays, setRestDays] = useState<number[]>(kind === "summer" ? [6, 7] : [7]);
  const [trips, setTrips] = useState<{ from: string; to: string; label: string }[]>([]);
  const [goal, setGoal] = useState("");

  const dowLabels = (() => {
    const base = new Date(Date.UTC(2024, 0, 1)); // Monday
    return DOW.map((d) =>
      new Intl.DateTimeFormat(i18n.language, { weekday: "short", timeZone: "UTC" })
        .format(new Date(base.getTime() + (d - 1) * 86400000)),
    );
  })();

  const steps: string[] = kind === "summer" ? ["debts", "period"] : ["scope", "period"];
  const stepId = steps[step];

  async function submit() {
    setBusy(true);
    try {
      const computedTitle =
        title.trim() ||
        (kind === "summer"
          ? t("breaks.hub.summer")
          : `${t(`breaks.hub.${kind}`)}${subject ? ` · ${subject}` : ""}`);
      const { id } = await create({
        data: {
          kind,
          title: computedTitle,
          subjectName: subject.trim() || null,
          startDate: startDate || new Date().toISOString().slice(0, 10),
          endDate: kind === "summer" ? endDate || null : unknownDate ? endDate || null : examDate || endDate || null,
          examDate: kind === "summer" ? null : unknownDate ? null : examDate || null,
          dailyMinutes,
          restDays,
          blackouts: trips.filter((x) => x.from && x.to),
          scope: kind === "summer" ? null : scope,
          ageBand,
          hasDebts: kind === "summer" ? (hasDebts ?? "unknown") : null,
          meta: goal ? { goal } : {},
        },
      });
      qc.invalidateQueries({ queryKey: ["study-periods"] });
      onClose();
      navigate({ to: "/breaks/$periodId", params: { periodId: id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("breaks.errors.generic"));
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    kind === "summer"
      ? Boolean(startDate && endDate)
      : Boolean(subject.trim() && (unknownDate ? endDate : examDate));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-3xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">{t(`breaks.hub.${kind}`)}</DialogTitle>
        </DialogHeader>

        {kind === "summer" && stepId === "debts" && (
          <div className="space-y-4">
            <p className="rounded-2xl bg-primary/10 p-3 text-sm leading-relaxed">{t("breaks.wizard.summerIntro")}</p>
            <div className="space-y-2">
              <Label className="text-xs">{t("breaks.wizard.debtsQ")}</Label>
              <div className="grid grid-cols-3 gap-2">
                {(["yes", "no", "unknown"] as const).map((v) => (
                  <Chip key={v} active={hasDebts === v} onClick={() => setHasDebts(v)}>
                    {t(`breaks.wizard.${v}`)}
                  </Chip>
                ))}
              </div>
              {hasDebts === "yes" && <p className="text-xs text-muted-foreground">{t("breaks.wizard.debtsHint")}</p>}
            </div>
            <AgePicker value={ageBand} onChange={setAgeBand} />
          </div>
        )}

        {kind !== "summer" && stepId === "scope" && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">{kind === "exam" ? t("breaks.wizard.examName") : t("breaks.wizard.subject")}</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={kind === "exam" ? t("breaks.wizard.examNamePh") : t("breaks.wizard.subjectPh")}
              />
            </div>
            {kind === "recovery" && (
              <div className="space-y-2">
                <Label className="text-xs">{t("breaks.wizard.scopeQ")}</Label>
                <div className="flex flex-wrap gap-2">
                  {(["topic", "topics", "term1", "term2", "year", "unknown"] as const).map((s) => (
                    <Chip key={s} active={scope === s} onClick={() => setScope(s)}>
                      {t(`breaks.wizard.scope.${s}`)}
                    </Chip>
                  ))}
                </div>
                {scope === "unknown" && <p className="text-xs text-muted-foreground">{t("breaks.wizard.unknownHint")}</p>}
              </div>
            )}
            {kind === "exam" && (
              <div className="space-y-1.5">
                <Label className="text-xs">{t("breaks.wizard.goal")}</Label>
                <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={t("breaks.wizard.goalPh")} />
              </div>
            )}
            <AgePicker value={ageBand} onChange={setAgeBand} />
          </div>
        )}

        {stepId === "period" && (
          <div className="space-y-4">
            {kind === "summer" ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("breaks.wizard.lastDay")}>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </Field>
                <Field label={t("breaks.wizard.firstDay")}>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </Field>
              </div>
            ) : (
              <div className="space-y-3">
                <Field label={t("breaks.wizard.testDateQ")}>
                  <Input type="date" value={examDate} disabled={unknownDate} onChange={(e) => setExamDate(e.target.value)} />
                </Field>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={unknownDate} onChange={(e) => setUnknownDate(e.target.checked)} />
                  {t("breaks.wizard.noTestDate")}
                </label>
                {unknownDate && (
                  <>
                    <p className="text-xs text-muted-foreground">{t("breaks.wizard.noTestDateHint")}</p>
                    <Field label={t("breaks.wizard.firstDay")}>
                      <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </Field>
                  </>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs">
                {t("breaks.wizard.dailyMax")}: <span className="font-semibold text-foreground">{dailyMinutes} min</span>
              </Label>
              <Slider value={[dailyMinutes]} min={10} max={180} step={5} onValueChange={(v) => setDailyMinutes(v[0])} />
            </div>

            <div className="space-y-2">
              <Label className="text-xs">{t("breaks.wizard.restDays")}</Label>
              <div className="flex flex-wrap gap-1.5">
                {DOW.map((d, i) => (
                  <Chip
                    key={d}
                    active={restDays.includes(d)}
                    onClick={() => setRestDays((r) => (r.includes(d) ? r.filter((x) => x !== d) : [...r, d]))}
                  >
                    {dowLabels[i]}
                  </Chip>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">{t("breaks.wizard.travel")}</Label>
              {trips.map((tr, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input type="date" value={tr.from} onChange={(e) => setTrips((p) => p.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))} />
                  <Input type="date" value={tr.to} onChange={(e) => setTrips((p) => p.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))} />
                  <Button size="icon" variant="ghost" onClick={() => setTrips((p) => p.filter((_, j) => j !== i))}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setTrips((p) => [...p, { from: "", to: "", label: "" }])}>
                <Plus className="mr-1 h-3.5 w-3.5" /> {t("breaks.wizard.addTravel")}
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          {step > 0 && (
            <Button variant="outline" className="flex-1" onClick={() => setStep((s) => s - 1)}>
              {t("breaks.wizard.back")}
            </Button>
          )}
          {step < steps.length - 1 ? (
            <Button className="flex-1" onClick={() => setStep((s) => s + 1)}>
              {t("breaks.wizard.next")}
            </Button>
          ) : (
            <Button className="flex-1" disabled={!canSubmit || busy} onClick={submit}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.wizard.create")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AgePicker({ value, onChange }: { value: AgeBand; onChange: (v: AgeBand) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <Label className="text-xs">{t("breaks.wizard.ageBand")}</Label>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
        {(["primary", "middle", "high", "university"] as const).map((b) => (
          <Chip key={b} active={value === b} onClick={() => onChange(b)}>
            {t(`breaks.wizard.${b}`)}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
        active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70 hover:bg-muted/70"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
